import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { appListenerMiddleware } from 'reduxTyped'
import { aiAutomationResetAll } from 'apps/aiAutomation/store/actions'
import { AiAlwaysOnAutomationsActions } from 'apps/aiAutomation/store/core'
import { AiAutomationToneOfVoiceState } from 'apps/aiAutomation/store/toneOfVoice/lib/types'
import { ToneOfVoiceSocketEventsSchemas } from 'apps/aiAutomation/store/toneOfVoice/schemas'
import { getUserId } from 'common/core/selectors/appSelectors'
import { AiToneOfVoiceApi } from 'shared/api/requests/aiToneOfVoice'
import { AiAutomationTovState, TovData, TovSample } from 'shared/api/requests/aiToneOfVoice/schemas'
import { makeCreateAsyncAction } from 'shared/lib/redux'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

let tovPollTimer: ReturnType<typeof setTimeout> | null = null
let getStateRef: (() => RootState) | null = null

const clearTovPoll = () => {
  if (tovPollTimer) {
    clearTimeout(tovPollTimer)
    tovPollTimer = null
  }
}

const scheduleTovPoll = (dispatch: AppDispatch) => {
  clearTovPoll()

  tovPollTimer = setTimeout(() => {
    if (!getStateRef) return
    const currentState = AiAutomationToneOfVoiceSelectors.getTovState(getStateRef())
    if (
      [AiAutomationTovState.CAPTURING_DATA, AiAutomationTovState.GENERATING_SAMPLES].includes(
        currentState,
      )
    ) {
      dispatch(getDataFx())
    }
  }, 60000)
}

const createAsyncAction = makeCreateAsyncAction({
  scope: 'ai',
  section: 'ai_always_on_automation',
})

const NAMESPACE = 'aiAutomationToneOfVoice'

export const AiAutomationToneOfVoiceInitialState: AiAutomationToneOfVoiceState = {
  state: AiAutomationTovState.NOT_STARTED,
  positivesCount: 0,
  totalCount: 0,
  samples: [],
  isOldVersion: false,
  activeSample: 0,
}

const { reducer, actions } = createSlice({
  initialState: AiAutomationToneOfVoiceInitialState,
  name: NAMESPACE,
  reducers: {
    dataUpdated(state, action: PayloadAction<TovData>) {
      state.state = action.payload.state
      state.positivesCount = action.payload.positives_count
      state.samples = action.payload.samples
      state.totalCount = action.payload.positives_count + action.payload.samples.length
      state.isOldVersion = Boolean(action.payload.is_old_version)
      state.activeSample = 0
    },

    stateUpdated(state, action: PayloadAction<AiAutomationTovState>) {
      state.state = action.payload
    },

    activeSampleMoved(state) {
      state.activeSample += 1
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDataFx.fulfilled, (state, action) => {
      state.state = action.payload.data.state
      state.positivesCount = action.payload.data.positives_count
      state.samples = action.payload.data.samples
      state.totalCount = action.payload.data.positives_count + action.payload.data.samples.length
      state.isOldVersion = Boolean(action.payload.data.is_old_version)
      state.activeSample = 0
    })

    builder.addCase(initializeFx.pending, (state) => {
      state.positivesCount = 0
      state.state = AiAutomationTovState.CAPTURING_DATA
      state.samples = []
      state.isOldVersion = false
      state.activeSample = 0
    })

    builder.addCase(aiAutomationResetAll, () => AiAutomationToneOfVoiceInitialState)
  },
})

const getDataFx = createAsyncAction(`${NAMESPACE}/getDataFx`, async () => {
  const { data } = await AiToneOfVoiceApi.getData()

  return data
})

const initializeFx = createAsyncAction(`${NAMESPACE}/initializeFx`, async () => {
  await AiToneOfVoiceApi.initialize()
})

const sendNegativeReactionFx = createAsyncAction(
  `${NAMESPACE}/negativeReactionFx`,
  async ({ sample, comment }: { sample: TovSample; comment: string }, { dispatch, getState }) => {
    const userId = getUserId(getState()) as number

    dispatch(actions.stateUpdated(AiAutomationTovState.GENERATING_SAMPLES))
    await AiToneOfVoiceApi.processFeedback({
      body: {
        user_id: userId,
        feedback: {
          is_positive: false,
          user_reaction: comment,
          sample,
        },
      },
    })
  },
)

const sendPositiveReactionFx = createAsyncAction(
  `${NAMESPACE}/positiveReactionFx`,
  async (sample: TovSample, { getState }) => {
    const userId = getUserId(getState()) as number

    await AiToneOfVoiceApi.processFeedback({
      body: {
        user_id: userId,
        feedback: {
          is_positive: true,
          sample,
        },
      },
    })
  },
)

export const AiAutomationToneOfVoiceActions = {
  getDataFx,
  initializeFx,
  sendNegativeReactionFx,
  sendPositiveReactionFx,

  activeSampleMoved: actions.activeSampleMoved,
}

anotherTabNotificationsListener.on(
  ToneOfVoiceSocketEventsSchemas.stateUpdated,
  ({ state }, dispatch) => {
    dispatch(actions.stateUpdated(state))

    clearTovPoll()
    if (
      [AiAutomationTovState.CAPTURING_DATA, AiAutomationTovState.GENERATING_SAMPLES].includes(state)
    ) {
      scheduleTovPoll(dispatch)
    }
  },
)

anotherTabNotificationsListener.on(
  ToneOfVoiceSocketEventsSchemas.samplesGenerated,
  ({ samples }, dispatch) => {
    dispatch(
      actions.dataUpdated({
        positives_count: 0,
        state: AiAutomationTovState.WAITING_USER_REACTION,
        samples,
      }),
    )
    clearTovPoll()
  },
)

appListenerMiddleware.startListening({
  actionCreator: aiAutomationResetAll,
  effect: () => {
    clearTovPoll()
    getStateRef = null
  },
})

appListenerMiddleware.startListening({
  actionCreator: AiAlwaysOnAutomationsActions.getConfigFx.fulfilled,
  effect: (data, { dispatch, getState }) => {
    getStateRef = getState

    const tov = data.payload.tone_of_voice
    if (!tov) {
      return
    }

    dispatch(actions.dataUpdated(tov))

    clearTovPoll()

    if (tov.state === AiAutomationTovState.WAITING_USER_REACTION) {
      dispatch(getDataFx())
      return
    }

    if (
      [AiAutomationTovState.CAPTURING_DATA, AiAutomationTovState.GENERATING_SAMPLES].includes(
        tov.state,
      )
    ) {
      scheduleTovPoll(dispatch)
    }
  },
})

export const AiAutomationToneOfVoiceReducer = reducer

const getState = (state: RootState) => state.aiAutomation.toneOfVoice

export const AiAutomationToneOfVoiceSelectors = {
  getState,

  getActiveSample: (state: RootState) => getState(state).activeSample,
  getPositiveCount: (state: RootState) => getState(state).positivesCount,
  getTotalCount: (state: RootState) => getState(state).totalCount,
  getSamples: (state: RootState) => getState(state).samples,
  getTovState: (state: RootState) => getState(state).state,
  getIsOldVersion: (state: RootState) => getState(state).isOldVersion,
}
